import { useEffect, useMemo, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import Loading from '../components/Loading';
import RequestForm from '../components/RequestForm';
import { captureException } from '../utils/captureException';
import { getRequestTypes, isSubmitButtonDisabled } from '../utils/form';
import { DPR_PREVIEW_CONFIG_UPDATE, DPR_PREVIEW_READY, getAllowedParentOrigins, parsePreviewConfig } from '../utils/previewMessages';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_CONFIG = {
  branding: {
    companyName: undefined,
    logoAltText: '',
    logoUrl: undefined
  },
  email: 'company-admin@company.com',
  requestTypes: {
    DELETE_REQUEST: true,
    EXPORT_REQUEST: true
  },
  urlSlug: 'url-slug',
  showCountrySetting: 'ENABLED_AND_ENFORCED',
  recaptchaEnabled: true
};
const mergeConfig = partial => Object.assign({}, DEFAULT_CONFIG, partial);
function PreviewContainer() {
  var _dataPrivacyConfig$br, _dataPrivacyConfig$br2;
  const [requestType, setRequestType] = useState(undefined);
  const [country, setCountry] = useState('');
  const [email, setEmail] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [dataPrivacyConfig, setDataPrivacyConfig] = useState(DEFAULT_CONFIG);
  const [hasInitialConfig, setHasInitialConfig] = useState(false);
  useEffect(() => {
    const allowedParents = getAllowedParentOrigins();
    const onMessage = event => {
      var _event$data;
      // Scope to the embedding parent frame specifically (not just any window on
      // an allowed origin), and ignore anything that isn't a config-update.
      if (event.source !== window.parent || !allowedParents.includes(event.origin) || ((_event$data = event.data) === null || _event$data === void 0 ? void 0 : _event$data.type) !== DPR_PREVIEW_CONFIG_UPDATE) {
        return;
      }
      const config = parsePreviewConfig(event.data.config);
      if (!config) {
        return;
      }
      setDataPrivacyConfig(mergeConfig(config));
      setHasInitialConfig(true);
    };
    window.addEventListener('message', onMessage);

    // Announce readiness so the host replies with the current config (covers the
    // iframe-mounted-after-host case). We don't know which hublet's app origin
    // the parent is, so ping each allowed origin — the browser only delivers to
    // the frame whose real origin matches; the rest are no-ops.
    if (window.parent !== window) {
      allowedParents.forEach(origin => {
        try {
          window.parent.postMessage({
            type: DPR_PREVIEW_READY
          }, origin);
        } catch (err) {
          captureException(err, ['PreviewContainer: readiness ping failed']);
        }
      });
    }
    return () => window.removeEventListener('message', onMessage);
  }, []);
  const requestTypes = useMemo(() => getRequestTypes(dataPrivacyConfig), [dataPrivacyConfig]);
  const submitButtonDisabled = useMemo(() => isSubmitButtonDisabled(dataPrivacyConfig, requestType, country, email, recaptchaToken), [dataPrivacyConfig, requestType, country, email, recaptchaToken]);
  if (!hasInitialConfig) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "PREVIEW_LOAD",
      children: /*#__PURE__*/_jsx(Loading, {})
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "PREVIEW_LOAD",
    children: /*#__PURE__*/_jsx(RequestForm, {
      dataTestId: "preview-form-container",
      country: country,
      email: email,
      logoAltText: dataPrivacyConfig === null || dataPrivacyConfig === void 0 || (_dataPrivacyConfig$br = dataPrivacyConfig.branding) === null || _dataPrivacyConfig$br === void 0 ? void 0 : _dataPrivacyConfig$br.logoAltText,
      logoUrl: (_dataPrivacyConfig$br2 = dataPrivacyConfig.branding) === null || _dataPrivacyConfig$br2 === void 0 ? void 0 : _dataPrivacyConfig$br2.logoUrl,
      onChangeCountry: setCountry,
      onChangeEmail: setEmail,
      onChangeRecaptchaToken: setRecaptchaToken,
      onChangeRequestType: setRequestType,
      onSubmit: () => {},
      requestType: requestType,
      requestTypes: requestTypes,
      submitButtonDisabled: submitButtonDisabled,
      businessEmail: dataPrivacyConfig === null || dataPrivacyConfig === void 0 ? void 0 : dataPrivacyConfig.email,
      isRecaptchaEnabled: !!(dataPrivacyConfig !== null && dataPrivacyConfig !== void 0 && dataPrivacyConfig.recaptchaEnabled),
      showCountrySetting: dataPrivacyConfig === null || dataPrivacyConfig === void 0 ? void 0 : dataPrivacyConfig.showCountrySetting
    })
  });
}
export default PreviewContainer;